import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  OutputDataObjectGroupProcessingStateType,
  DirectoryTableProps,
  formatCodeName,
  formatDate,
  formatDateTimeShort,
  getDayjs,
  getDayjsNow,
  getMapBy,
  registerTableFilter,
  Table,
  TextEllipsis,
  useSystemClassifierItems,
  useSystemClassifierItemsBy,
  useSystemClassifiers,
  useSystemClassifierSelectOptions,
} from '@atlas-frontend/sdk';
import {
  CellContext,
  createColumnHelper,
  FilterFn,
} from '@tanstack/react-table';
import { FC, memo, useMemo } from 'react';
import translations from './OutputDataDirectoryTable.translations.json';
import { OutputGroupDescriptionFilterInput } from './OutputGroupDescriptionFilterInput';
import { colorAccent1 } from '@atlas-frontend/design-tokens';
import { OutputDataActionCell } from './OutputDataActionCell';
import {
  ActiveProcessingCell,
  getActiveProcessingTypeLabel,
} from './ActiveProcessingCell';
import { VersionCell } from './VersionCell';
import { ErrorLevel } from './ErrorLevel';
import type { OutputDataAction } from './OutputDataDirectory';

export const MIXED_OUTPUT_TYPE_FILTER_VALUE = 'MIXED';

const { addTranslations, useTranslation } = i18nNsFactory(
  'output-data-directory-table',
);
addTranslations(translations);

const columnHelper =
  createColumnHelper<OutputDataObjectGroupProcessingStateType>();

export type OutputDataDirectoryTableProps =
  DirectoryTableProps<OutputDataObjectGroupProcessingStateType> & {
    onAction: (action: OutputDataAction) => void;
    onActionDropdownOpenChange?: (open: boolean) => void;
  };

export function OutputDataDirectoryTable(props: OutputDataDirectoryTableProps) {
  const { onAction, onActionDropdownOpenChange } = props;
  const { t } = useTranslation();
  const { DOMAIN: domainOptions, FREQUENCY: frequencyOptions } =
    useSystemClassifierSelectOptions(['DOMAIN', 'FREQUENCY']);
  const {
    PROCESSING_TYPE: { systemClassifierItems: processingTypes },
    PROCESSING_METHOD: { systemClassifierItems: processingMethods },
    DATA_PROCESSING_SCOPE: { systemClassifierItems: processingScopes },
    PROC_VAL_ERROR_LEVEL: { systemClassifierItems: validationErrorLevels },
  } = useSystemClassifiers(
    'PROCESSING_TYPE',
    'PROCESSING_METHOD',
    'DATA_PROCESSING_SCOPE',
    'PROC_VAL_ERROR_LEVEL',
  );
  const {
    DOMAIN: domainsById,
    FREQUENCY: frequenciesById,
    OUTPUT_TYPE: outputTypesById,
  } = useSystemClassifierItemsBy(['DOMAIN', 'FREQUENCY', 'OUTPUT_TYPE'], 'id');

  const classifierMaps = useMemo(
    () => ({
      processingTypes: getMapBy(processingTypes, 'id'),
      processingMethods: getMapBy(processingMethods, 'id'),
      processingScopes: getMapBy(processingScopes, 'id'),
      validationErrorLevels: getMapBy(validationErrorLevels, 'id'),
    }),
    [
      processingMethods,
      processingScopes,
      processingTypes,
      validationErrorLevels,
    ],
  );

  const outputTypeClfItems = useSystemClassifierItems('OUTPUT_TYPE');
  const outputTypeFilterOptions = useMemo(
    () => [
      ...outputTypeClfItems,
      {
        id: MIXED_OUTPUT_TYPE_FILTER_VALUE,
        code: 'MIXED',
        name: t('outputType.MIXED'),
      },
    ],
    [outputTypeClfItems, t],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (rowData) =>
          rowData.domainId
            ? domainsById.get(rowData.domainId)?.name
            : undefined,
        {
          id: 'domainId',
          header: t('column.domain'),
          filterFn: 'includesOption',
          meta: {
            className: 'sticky-left',
            width: '16ch',
          },
        },
      ),
      columnHelper.accessor(
        (rowData) =>
          formatCodeName({
            code: rowData.dataObjectGroupCode,
            name: rowData.dataObjectGroupName,
          }),
        {
          id: 'name',
          header: t('column.name'),
          filterFn: 'outputSampleSelect',
          meta: {
            className: 'sticky-left',
            minWidth: '28ch',
          },
        },
      ),
      columnHelper.accessor(
        (rowData) =>
          rowData.outputTypeId == null
            ? t('outputType.MIXED')
            : outputTypesById.get(rowData.outputTypeId)?.name,
        {
          id: 'outputTypeId',
          header: t('column.outputType'),
          filterFn: 'includesOption',
          meta: {
            width: '16ch',
          },
        },
      ),
      columnHelper.accessor((rowData) => formatDate(rowData.valueDate), {
        id: 'valueDate',
        header: t('column.valueDate'),
        filterFn: 'valueDateSelect',
        meta: {
          width: '18ch',
        },
      }),
      columnHelper.accessor(
        (rowData) =>
          rowData.frequencyId
            ? frequenciesById.get(rowData.frequencyId)?.name
            : undefined,
        {
          id: 'frequencyId',
          header: t('column.frequency'),
          filterFn: 'includesOption',
          meta: {
            width: '16ch',
          },
        },
      ),
      columnHelper.accessor('responsibleUserUsername', {
        id: 'username',
        header: t('column.responsible'),
        meta: {
          width: '16ch',
        },
      }),
      columnHelper.accessor((rowData) => rowData.dueDateTime, {
        id: 'dueDateTime',
        header: t('column.dueDateTime'),
        cell: DueDateTimeCell,
        filterFn: 'validPeriod',
        meta: {
          width: '16ch',
        },
      }),
      columnHelper.accessor(
        (rowData) => formatDateTimeShort(rowData.lastProcessingEndTime),
        {
          id: 'lastProcessingEndTime',
          header: t('column.lastProcessingEndTime'),
          enableColumnFilter: false,
          meta: {
            width: '26ch',
          },
        },
      ),
      columnHelper.accessor('versionValidationErrorLevelId', {
        header: t('column.versionValidationErrors'),
        cell: (props) => (
          <ErrorLevel
            {...props.row.original}
            type="processingGroup"
            typeId={props.row.original.id}
            validationErrorLevels={classifierMaps.validationErrorLevels}
            hasAccess={props.row.original.hasAccess ?? true}
          />
        ),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: '24ch',
        },
      }),
      columnHelper.accessor(
        ({ processingStateCode, processing }) =>
          getActiveProcessingTypeLabel({
            processingStateCode,
            processing,
            ...classifierMaps,
          }),
        {
          id: 'processingStateCode',
          header: t('column.processingState'),
          cell: ActiveProcessingCell as any,
          filterFn: 'includesOption',
          enableSorting: false,
          meta: {
            width: '16ch',
          },
        },
      ),
      columnHelper.accessor('version', {
        header: t('column.version'),
        cell: (props) => (
          <VersionCell
            version={props.row.original.version}
            versionStatus={props.row.original.versionStatus}
          />
        ),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: '20ch',
        },
      }),
      columnHelper.display({
        id: 'operations',
        header: t('column.operations'),
        cell: (props) => (
          <OutputDataActionCell
            {...props}
            onAction={onAction}
            onDropdownOpenChange={onActionDropdownOpenChange}
          />
        ),
        meta: {
          width: 72,
          className: 'sticky-right',
        },
      }),
    ],
    [
      classifierMaps,
      domainsById,
      frequenciesById,
      onAction,
      onActionDropdownOpenChange,
      outputTypesById,
      t,
    ],
  );

  const options = useMemo(
    () => ({
      domainId: domainOptions.map((domain) => ({
        id: domain.value,
        name: domain.label,
      })),
      frequencyId: frequencyOptions.map((frequency) => ({
        id: frequency.value,
        name: frequency.label,
      })),
      outputTypeId: outputTypeFilterOptions
        .map((item) => ({
          id: item.id,
          name: item.name,
        }))
        .filter(
          (item) =>
            item.id === MIXED_OUTPUT_TYPE_FILTER_VALUE ||
            (typeof item.id === 'number' &&
              outputTypesById.get(item.id)?.code !== 'INTERMEDIATE'),
        ),
      processingStateCode: [
        {
          id: 'IN_PROGRESS',
          name: t('processingState.IN_PROGRESS'),
        },
        {
          id: 'VALIDATION_IN_PROGRESS',
          name: t('processingState.VALIDATION_IN_PROGRESS'),
        },
        {
          id: 'VALIDATION_READY',
          name: t('processingState.VALIDATION_READY'),
        },
      ],
    }),
    [
      domainOptions,
      frequencyOptions,
      outputTypeFilterOptions,
      outputTypesById,
      t,
    ],
  );

  return (
    <Table
      name="output-data-directory-table"
      columns={columns}
      hideClearFilters
      manualFiltering
      manualSorting
      options={options}
      scrollContainerSelector="#output-data-directory-content"
      sticky
      virtual
      {...props}
    />
  );
}

const DueDateTimeCell: FC<
  CellContext<OutputDataObjectGroupProcessingStateType, any>
> = memo((props) => {
  const { dueDateTime, versionConfirmed } = props.row.original;
  const isWarning =
    dueDateTime &&
    !versionConfirmed &&
    getDayjs(dueDateTime)?.isBefore(getDayjsNow());

  return (
    <TextEllipsis style={{ color: isWarning ? colorAccent1 : undefined }}>
      {formatDateTimeShort(props.row.original.dueDateTime)}
    </TextEllipsis>
  );
});

declare module '@tanstack/react-table' {
  interface FilterFns {
    outputSampleSelect: FilterFn<any>;
  }
}
registerTableFilter<OutputDataObjectGroupProcessingStateType>(
  'outputSampleSelect',
  () => {
    // Server-side filtering
    return true;
  },
  OutputGroupDescriptionFilterInput,
);
